import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Alert,
  Spinner,
} from "reactstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { addMember } from "../../../slices/member/action";

const CreateMembers = (props) => {
  const { modalData, modal, setApiSuccess, handleModal } = props;
  const { name, email, mobile, _id, role, isActive } = modalData || {};
  const isUpdateMode = !!email;
  const [isLoading, setIsLoading] = useState(false);
  const [isMemberActive, setIsMemberActive] = useState(isActive || false);

  // Validation schema
  const memeberSchema = yup.object().shape({
    memberName: yup.string().required("Member name is required"),
    email: yup
      .string()
      .email("Invalid email format")
      .required("Email is required"),
    mobile: yup
      .string()
      .matches(/^\d{10}$/, "Mobile number must be 10 digits")
      .required("Mobile number is required"),
    role: yup.string().required("Role is required"),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      memberName: "",
      email: "",
      mobile: "",
      role: "",
      isActive: false,
    },
    resolver: yupResolver(memeberSchema),
  });

  // Populate form fields when modalData changes (edit mode)
  useEffect(() => {
    if (modalData) {
      setIsMemberActive(isActive);
      reset({
        memberName: name || "",
        email: email || "",
        mobile: mobile || "",
        role: role || "",
        isActive: isMemberActive || false,
      });
    } else {
      // Clear form fields in create mode
      setIsMemberActive(false);
      reset({
        memberName: "",
        email: "",
        mobile: "",
        role: "",
        isActive: false,
      });
    }
  }, [modalData, reset]);

  const addorUpdateMember = async (data) => {
    const updatedData = { ...data, name: data.memberName };
    delete updatedData.memberName;
    const response = await addMember(updatedData);
    if (response?._id) {
      setApiSuccess(true);
      handleModal();
      setIsLoading(false);
      reset(); // Reset form fields
    }
    setIsMemberActive(false);
    setIsLoading(false);
  };

  // Submit handler
  const onSubmit = (data) => {
    setIsLoading(true);
    if (isUpdateMode) {
      data = { ...data, _id, isActive: isMemberActive };
    }
    addorUpdateMember(data);
  };

  return (
    <>
      <Button
        size="sm"
        color="info"
        onClick={() => handleModal()}
        className="d-flex align-items-center gap-1"
      >
        <i className="bx bx-plus"></i> Create New Member
      </Button>
      {/* Create Modal */}
      <Modal
        size="lg"
        isOpen={modal}
        toggle={() => {
          handleModal();
        }}
        backdrop="static"
      >
        <div className="modal-header border-bottom">
          <h6 className="modal-title">Add New Member</h6>
          <button
            type="button"
            className="btn-close"
            onClick={() => handleModal()}
          ></button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <Row>
              {/* First Name */}
              <Col md={6} className="mb-3">
                <label htmlFor="memberName" className="form-label">
                  Full Name
                </label>
                <input
                  id="memberName"
                  type="text"
                  className={`form-control ${
                    errors.memberName ? "is-invalid" : ""
                  }`}
                  {...register("memberName")}
                />
                {errors.memberName && (
                  <div className="invalid-feedback">
                    {errors.memberName.message}
                  </div>
                )}
              </Col>

              {/* Email */}
              <Col md={6} className="mb-3">
                <label htmlFor="email" className="form-label">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                  {...register("email")}
                  disabled={isUpdateMode ? true : false}
                />
                {errors.email && (
                  <div className="invalid-feedback">{errors.email.message}</div>
                )}
              </Col>

              {/* Mobile Number */}
              <Col md={6} className="mb-3">
                <label htmlFor="mobile" className="form-label">
                  Mobile Number
                </label>
                <input
                  id="mobile"
                  type="text"
                  className={`form-control ${
                    errors.mobile ? "is-invalid" : ""
                  }`}
                  {...register("mobile")}
                />
                {errors.mobile && (
                  <div className="invalid-feedback">
                    {errors.mobile.message}
                  </div>
                )}
              </Col>

              {/* Role */}
              <Col md={6} className="mb-3">
                <label htmlFor="role" className="form-label">
                  Member Role
                </label>
                <select
                  id="role"
                  className={`form-select ${errors.role ? "is-invalid" : ""}`}
                  {...register("role")}
                >
                  <option value="">Select Role</option>
                  <option value="admin">Admin</option>
                  <option value="member">User</option>
                </select>
                {errors.role && (
                  <div className="invalid-feedback">{errors.role.message}</div>
                )}
              </Col>

              {/* Active Toggle */}
              <Col md={12} className="mb-3 d-flex align-items-center gap-2">
                <label htmlFor="activeStatus" className="form-label mb-0">
                  Active
                </label>
                <div>
                  <Button
                    size="sm"
                    color="transparent"
                    className={`shadow-none d-flex gap-2 align-items-center justify-content-center fs-16 ${
                      isMemberActive ? "text-success" : "text-danger"
                    }`}
                    onClick={() => setIsMemberActive(!isMemberActive)}
                  >
                    <i
                      className={`fs-32 ${
                        isMemberActive ? "ri-toggle-fill" : "ri-toggle-line"
                      }`}
                    ></i>
                    {isMemberActive ? "Enabled" : "Disabled"}
                  </Button>
                </div>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button outline color="dark" onClick={() => handleModal()}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={isLoading ? "disabled" : null}
            >
              Save Changes{" "}
              {isLoading ? <Spinner size="sm" color="light" /> : null}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </>
  );
};

export default CreateMembers;
